import { Buildings, CloudArrowUp, Plus } from '@phosphor-icons/react';
import { useCallback, useEffect, useState } from 'react';
import p from './Projects.module.css';
import { createOrganisation, createProject, listOrganisations, listProjects, loadProject, type CloudProject, type Organisation } from '../auth/cloud';
import type { ProjectDoc } from '../model/types';
import { useStore } from '../state/store';
import { Button } from '../ui/kit';

type Load<T> = { at: 'loading' } | { at: 'ready'; value: T } | { at: 'error'; message: string };

/** First sign-in: there is no organisation yet, so make one. */
function FirstOrganisation({ onCreated }: { onCreated: () => void }) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    setError(null);
    const result = await createOrganisation(name);
    setBusy(false);
    if ('error' in result) setError(result.error);
    else onCreated();
  };
  return (
    <section className={p.panel}>
      <h2><Buildings size={18} /> Name your organisation</h2>
      <p className={p.panelLede}>
        Projects belong to an organisation, usually your firm. Colleagues you invite share its projects;
        nobody outside it can see them.
      </p>
      <form className={p.inline} onSubmit={submit}>
        <input className={p.input} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Riverside Safety Engineering" aria-label="Organisation name" autoFocus disabled={busy} />
        <Button variant="primary" type="submit" disabled={busy || !name.trim()}>{busy ? 'Creating…' : 'Create'}</Button>
      </form>
      {error && <p className={p.error} role="alert">{error}</p>}
    </section>
  );
}

export function CloudProjects({ localDoc }: { localDoc: ProjectDoc | null }) {
  const [orgs, setOrgs] = useState<Load<Organisation[]>>({ at: 'loading' });
  const [projects, setProjects] = useState<Load<CloudProject[]>>({ at: 'loading' });
  const [orgId, setOrgId] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const refreshOrgs = useCallback(async () => {
    const result = await listOrganisations();
    if ('error' in result) return setOrgs({ at: 'error', message: result.error });
    setOrgs({ at: 'ready', value: result.organisations });
    setOrgId((id) => id ?? result.organisations[0]?.id ?? null);
  }, []);

  useEffect(() => { void refreshOrgs(); }, [refreshOrgs]);

  const refreshProjects = useCallback(async (id: string) => {
    setProjects({ at: 'loading' });
    const result = await listProjects(id);
    setProjects('error' in result ? { at: 'error', message: result.error } : { at: 'ready', value: result.projects });
  }, []);

  useEffect(() => { if (orgId) void refreshProjects(orgId); }, [orgId, refreshProjects]);

  const open = async (project: CloudProject) => {
    setBusy(project.id);
    const result = await loadProject(project.id);
    setBusy(null);
    if ('error' in result) return useStore.getState().notify(result.error);
    useStore.getState().openProject(project.id, result.doc, 'cloud', result.version);
  };

  const add = async (doc: ProjectDoc, label: string) => {
    if (!orgId) return;
    setBusy(label);
    const result = await createProject(orgId, doc);
    setBusy(null);
    if ('error' in result) return useStore.getState().notify(result.error);
    await refreshProjects(orgId);
    useStore.getState().openProject(result.id, doc, 'cloud', 1);
  };

  if (orgs.at === 'loading') return <section className={p.panel}><p className={p.muted}>Loading your organisations…</p></section>;
  if (orgs.at === 'error') return <section className={p.panel}><p className={p.error}>{orgs.message}</p></section>;
  if (orgs.value.length === 0) return <FirstOrganisation onCreated={() => void refreshOrgs()} />;

  const current = orgs.value.find((o) => o.id === orgId) ?? orgs.value[0]!;
  const canWrite = current.role !== 'viewer';
  const fmt = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' });

  return (
    <section className={p.panel}>
      <div className={p.panelHead}>
        <h2><Buildings size={18} /> {orgs.value.length > 1 ? (
          <select className={p.orgSelect} value={current.id} onChange={(e) => setOrgId(e.target.value)} aria-label="Organisation">
            {orgs.value.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        ) : current.name}</h2>
        <span className={p.role}>{current.role}</span>
        {canWrite && (
          <span className={p.panelActions}>
            {localDoc && (
              <Button icon={<CloudArrowUp size={15} />} disabled={busy !== null} onClick={() => void add(localDoc, 'upload')}>
                {busy === 'upload' ? 'Copying…' : 'Copy the open project here'}
              </Button>
            )}
            <Button variant="primary" icon={<Plus size={15} />} disabled={busy !== null} onClick={() => void add(blankDoc(), 'new')}>
              {busy === 'new' ? 'Creating…' : 'New project'}
            </Button>
          </span>
        )}
      </div>

      {projects.at === 'loading' && <p className={p.muted}>Loading projects…</p>}
      {projects.at === 'error' && <p className={p.error}>{projects.message}</p>}
      {projects.at === 'ready' && projects.value.length === 0 && (
        <p className={p.muted}>No projects in {current.name} yet. {canWrite ? 'Create one, or copy a project from this browser.' : 'Ask an editor to add one.'}</p>
      )}
      {projects.at === 'ready' && projects.value.length > 0 && (
        <table className={p.table}>
          <thead><tr><th>Name</th><th>Client</th><th>Site</th><th className={p.num}>Sources</th><th>Last changed</th></tr></thead>
          <tbody>
            {projects.value.map((x) => (
              <tr key={x.id} tabIndex={0} onClick={() => void open(x)} onKeyDown={(e) => { if (e.key === 'Enter') void open(x); }}>
                <td className={p.name}>{busy === x.id ? 'Opening…' : x.name}</td>
                <td>{x.client ?? '—'}</td><td>{x.site ?? '—'}</td>
                <td className={p.num}>{x.sourceCount}</td><td>{fmt.format(new Date(x.updatedAt))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function blankDoc(): ProjectDoc {
  return {
    schemaVersion: 1,
    meta: { name: 'Untitled project' },
    settings: { ambientTemperatureK: 293.15, ambientPressurePa: 101_325, criticalConcentrationFraction: 0.25, lflSafetyFactor: 1 },
    substances: [], areas: [], sources: [], equipment: [], objects: [],
  };
}
